import React, { useMemo, useRef } from 'react';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Grid from '@material-ui/core/Grid';
import Hidden from '@material-ui/core/Hidden';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import { Doughnut } from 'react-chartjs-2';
import 'chartjs-plugin-doughnutlabel';
import { DocumentTemplateType, INumberedDocumentView } from '../../api-client/autogenerated';
import { computeStatusBucketCounts, STATUS_BUCKET_LABELS } from '../../scripts/utils';
import StatusPill from '../custom-components/StatusPill';
import { fixedLegendWidthPlugin } from './fixedLegendWidthPlugin';

// Same red used for the Overdue square and every table's Overdue pill.
const OVERDUE_COLOR = '#E2445C';
const OVERDUE_LABEL = 'Overdue';

interface Props {
  documents: INumberedDocumentView[];
  documentTemplateIdToName: Record<string, DocumentTemplateType>;
  /** Currently active status-bucket filters (STATUS_BUCKET_LABELS labels); same array the filter
   * dialog's Status checkboxes read/write, so a click here and a checkbox there can't disagree. */
  activeStatuses: string[];
  activeOverdue: boolean;
  /** Toggles the bucket's membership in the parent's filter array. */
  onStatusClick: (bucketLabel: string) => void;
  onOverdueClick: () => void;
  /** Surface color behind the chart, matched to whatever this card is rendered on — the My Work
   * page's #FAFAFA canvas or the dashboard card's white one — so the chart reads as part of that
   * surface rather than as a tinted block sitting on it. */
  backgroundColor: string;
}

const useStyles = makeStyles({
  root: { boxShadow: 'none' },
  flexColumn: {
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'nowrap',
  },
  overdueSquare: {
    width: 154,
    height: 154,
    backgroundColor: 'rgba(9,71,185,0.4)',
    borderRadius: 8,
    display: 'grid',
    placeItems: 'center',
    padding: 10,
    boxSizing: 'border-box',
    color: '#464546',
    border: '1px solid',
    cursor: 'pointer',
  },
});

/**
 * Status breakdown for the My Work page's mixed-document-type table. Unlike DashboardDoughnut
 * (which fetches its own single-type document list and labels slices with that type's specific
 * status names), this groups whatever documents it's given into the broad status buckets in
 * STATUS_BUCKET_LABELS — the same buckets, colored by the same getTaskStatusColors function every
 * table row's status pill/color bar already uses, so this can never fall out of sync with the
 * table. The doughnut/legend/click/hover behavior mirrors DashboardDoughnut's taskPageStats mode
 * exactly (same chart.js legend + onClick + hover wiring), so clicking a status slice or legend
 * entry (or the Overdue square) reports it to the parent, which owns the actual filter state and
 * re-filters the table.
 */
export default function MyTasksStatusSummary({
  documents,
  documentTemplateIdToName,
  activeStatuses,
  activeOverdue,
  onStatusClick,
  onOverdueClick,
  backgroundColor,
}: Props) {
  const classes = useStyles();
  const chartRef = useRef<Doughnut>(null);

  const { counts, overdueCount } = useMemo(
    () => computeStatusBucketCounts(documents, documentTemplateIdToName),
    [documents, documentTemplateIdToName],
  );

  const total = documents.length;

  // Overdue is appended as its own slice (in addition to the Overdue square below), matching
  // DashboardDoughnut's convention of listing Overdue as the last slice — a doc that's both, say,
  // "Under Review / In Progress" and overdue contributes to both slices, so the pie's total can
  // exceed the center "total" label; that overlap is intentional, not a bug.
  const pieSlices = [
    ...STATUS_BUCKET_LABELS.filter((b) => counts[b.label] > 0),
    ...(overdueCount > 0 ? [{ color: OVERDUE_COLOR, label: OVERDUE_LABEL }] : []),
  ];
  const sliceCount = (label: string) => (label === OVERDUE_LABEL ? overdueCount : counts[label]);

  const pieData = {
    labels: pieSlices.map((b) => `${b.label} ${sliceCount(b.label)}`),
    datasets: [
      {
        data: pieSlices.map((b) => sliceCount(b.label)),
        backgroundColor: pieSlices.map((b) => b.color),
      },
    ],
  };

  const emptyData = {
    labels: ['No tasks'],
    datasets: [{ data: [1], backgroundColor: ['#EDECEC'] }],
  };

  const plugins = {
    doughnutlabel: {
      labels: [
        { text: total, font: { size: 44, lineHeight: '52px' } },
        { text: 'total', font: { size: 26, lineHeight: '30px' } },
      ],
    },
  };

  // Slice/legend labels carry a trailing " <count>" (e.g. "Completed 3"); strip it back to the
  // bucket name shared with STATUS_BUCKET_LABELS. The Overdue slice isn't a real status bucket —
  // route it to the same callback the Overdue square already uses.
  const handleSliceClick = (label: string) => {
    const bucketLabel = label.slice(0, label.lastIndexOf(' '));
    if (bucketLabel === OVERDUE_LABEL) {
      onOverdueClick();
      return;
    }
    onStatusClick(bucketLabel);
  };

  const doughnutOptions =
    total === 0
      ? {
          animation: { duration: 0 },
          maintainAspectRatio: false,
          responsive: true,
          plugins,
          events: [],
        }
      : {
          animation: { duration: 0 },
          maintainAspectRatio: false,
          responsive: true,
          plugins,
          tooltips: {
            callbacks: {
              label: (tooltipItem: { index: number }, data: { labels: string[] }) => {
                return data.labels[tooltipItem.index];
              },
            },
          },
          hover: {
            onHover: (e: any, other: any) => {
              e.target.style.cursor = other.length ? 'pointer' : 'default';
            },
          },
          onClick: (e: any, items: any[]) => {
            if (items.length > 0) handleSliceClick(items[0]._view.label);
          },
        };

  const legendOptions = {
    onClick: (event: MouseEvent, item: { text: string }) => handleSliceClick(item.text),
    onHover: (e: any) => {
      e.target.style.cursor = 'pointer';
    },
  };

  return (
    <Card className={classes.root} style={{ backgroundColor }}>
      <CardContent className={classes.flexColumn} style={{ padding: '0px' }}>
        <Typography
          style={{ fontWeight: 600, fontSize: 16, color: '#464546', padding: '8px 8px 0' }}
        >
          By Status
        </Typography>
        <Grid container justify="flex-start" spacing={1} alignItems="stretch">
          <Grid item xs={12} style={{ padding: 8, maxWidth: 560 }}>
            <Hidden xsDown>
              <div style={{ width: '99%' }}>
                <Doughnut
                  // Chart.js only binds DOM event listeners (click/hover/etc.) once, at
                  // construction, from whatever `options.events` is at that moment — a later
                  // options update (e.g. once `documents` finishes loading) never re-binds them.
                  // Since this chart renders before `documents` loads (total === 0, events
                  // disabled for the empty-state placeholder), keying on that transition forces a
                  // full remount — and a fresh Chart.js instance with real events — once data
                  // actually arrives, instead of leaving a permanently inert chart.
                  key={total === 0 ? 'empty' : 'data'}
                  ref={chartRef}
                  data={total === 0 ? emptyData : pieData}
                  width={300}
                  // The fixed legend width plus this column's capped width leaves the ring
                  // width-constrained at ~245px in practice (see fixedLegendWidthPlugin) — a
                  // canvas height of 300 left ~55px of empty space above/below the ring inside
                  // its own box, on top of the card padding tightened separately.
                  height={260}
                  legend={{ position: 'right', ...legendOptions }}
                  options={doughnutOptions}
                  plugins={[fixedLegendWidthPlugin]}
                />
              </div>
            </Hidden>
            <Hidden smUp>
              <div style={{ width: '99%' }}>
                <Doughnut
                  key={total === 0 ? 'empty' : 'data'}
                  data={total === 0 ? emptyData : pieData}
                  width={300}
                  height={300}
                  legend={{ position: 'bottom', ...legendOptions }}
                  options={doughnutOptions}
                />
              </div>
            </Hidden>
          </Grid>
          {/*<Grid item xs={12} sm={4} style={{ display: 'flex', alignItems: 'center' }}>*/}
          {/*  <div*/}
          {/*    style={{*/}
          {/*      width: '100%',*/}
          {/*      display: 'flex',*/}
          {/*      justifyContent: 'center',*/}
          {/*      alignItems: 'center',*/}
          {/*      minHeight: 200,*/}
          {/*      boxSizing: 'border-box',*/}
          {/*      color: '#464546',*/}
          {/*      gap: 10,*/}
          {/*    }}*/}
          {/*  >*/}
          {/*    <div*/}
          {/*      className={classes.overdueSquare}*/}
          {/*      onClick={onOverdueClick}*/}
          {/*      role="button"*/}
          {/*      aria-label={activeOverdue ? 'Clear overdue filter' : 'Filter by overdue tasks'}*/}
          {/*      style={*/}
          {/*        activeOverdue ? { outline: '2px solid #0947B9', outlineOffset: -1 } : undefined*/}
          {/*      }*/}
          {/*    >*/}
          {/*      <div style={{ textAlign: 'center' }}>*/}
          {/*        <div style={{ marginBottom: 20 }}>*/}
          {/*          <StatusPill backgroundColor="#E2445C">Overdue</StatusPill>*/}
          {/*        </div>*/}
          {/*        <div*/}
          {/*          style={{*/}
          {/*            fontSize: 35,*/}
          {/*            marginBottom: 15,*/}
          {/*            fontWeight: 400,*/}
          {/*            color: overdueCount > 0 ? '#ED3F26' : undefined,*/}
          {/*          }}*/}
          {/*        >*/}
          {/*          {overdueCount}*/}
          {/*        </div>*/}
          {/*        <div style={{ fontSize: 14 }}>total</div>*/}
          {/*      </div>*/}
          {/*    </div>*/}
          {/*  </div>*/}
          {/*</Grid>*/}
        </Grid>
      </CardContent>
    </Card>
  );
}
